import { useState, useEffect, ComponentType } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  DollarSign,
  FileText,
  AlertTriangle,
  TrendingUp,
  Activity,
  Calendar
} from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { partnerAdminApi } from '../services/api'

// Local StatCard component (replacing @ngc-medical/shared)
interface StatCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: ComponentType<{ className?: string }>
  iconColor?: string
  trend?: {
    value: number
    label: string
    isPositiveGood?: boolean
  }
}

function StatCard({ title, value, subtitle, icon: Icon, iconColor = 'text-blue-600', trend }: StatCardProps) {
  const trendIsPositive = trend && trend.value > 0
  const trendIsGood = trend && ((trend.isPositiveGood && trendIsPositive) || (!trend.isPositiveGood && !trendIsPositive))

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className={`w-4 h-4 ${trendIsGood ? 'text-green-500' : 'text-red-500'}`} />
              <span className={`text-sm font-medium ${trendIsGood ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {trendIsPositive ? '+' : ''}{trend.value}%
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{trend.label}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-gray-50 dark:bg-gray-700 ${iconColor}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  )
}

interface DashboardStats {
  totalClients: number
  activeClients: number
  monthlyRevenue: number
  pendingDocuments: number
  upcomingObligations: number
  complianceRate: number
  supportTickets: number
  revenueGrowth: number
}

interface PartnerInfo {
  id: string
  name: string
  slug: string
  status: string
}

interface RecentActivity {
  id: string
  type: 'document' | 'payment' | 'obligation' | 'client'
  description: string
  client: string
  timestamp: string
  status: 'success' | 'warning' | 'error' | 'info'
}

interface UpcomingObligation {
  id: string
  client: string
  type: string
  description: string
  dueDate: string
  amount: number
  daysUntilDue: number
}

export function PartnerDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [activities, setActivities] = useState<RecentActivity[]>([])
  const [obligations, setObligations] = useState<UpcomingObligation[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const data = await partnerAdminApi.dashboard.get()

        const apiStats: DashboardStats = {
          totalClients: data.metrics.totalClients,
          activeClients: data.metrics.activeClients,
          monthlyRevenue: data.metrics.monthlyRevenue,
          pendingDocuments: data.metrics.pendingTasks,
          upcomingObligations: data.upcomingObligations?.length ?? 0,
          complianceRate: data.metrics.complianceRate ?? 0,
          supportTickets: 0,
          revenueGrowth: data.metrics.revenueGrowth ?? 0
        }

        setStats(apiStats)

        // Usar atividades reais da API
        const realActivities: RecentActivity[] = (data.recentActivities || []).map((a) => ({
          id: a.id,
          type: a.category as RecentActivity['type'],
          description: a.description,
          client: a.clientName,
          timestamp: new Date(a.timestamp).getTime(),
          status: a.priority === 'high' ? 'warning' : 'success' as RecentActivity['status']
        }))
        setActivities(realActivities)

        // Usar obrigacoes reais da API
        const realObligations: UpcomingObligation[] = (data.upcomingObligations || []).map((o) => ({
          id: o.id,
          client: o.client,
          type: o.type,
          description: o.description || o.type,
          dueDate: o.dueDate,
          amount: o.amount,
          daysUntilDue: Math.ceil((new Date(o.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        }))
        setObligations(realObligations)
        setLoading(false)

      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        const fallbackStats: DashboardStats = {
          totalClients: 0,
          activeClients: 0,
          monthlyRevenue: 0,
          pendingDocuments: 0,
          upcomingObligations: 0,
          complianceRate: 0,
          supportTickets: 0,
          revenueGrowth: 0
        }
        setStats(fallbackStats)
        setActivities([])
        setObligations([])
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'document': return <FileText className="w-4 h-4" />
      case 'payment': return <DollarSign className="w-4 h-4" />
      case 'obligation': return <Calendar className="w-4 h-4" />
      case 'client': return <Users className="w-4 h-4" />
      default: return <Activity className="w-4 h-4" />
    }
  }

  const getActivityColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
      case 'warning': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30'
      case 'error': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
      case 'info': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30'
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700'
    }
  }

  const getTimeAgo = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Agora ha pouco'
    if (diffInHours === 1) return 'Ha 1 hora'
    if (diffInHours < 24) return `Ha ${diffInHours} horas`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays === 1) return 'Ha 1 dia'
    return `Ha ${diffInDays} dias`
  }

  const getUrgencyColor = (days: number) => {
    if (days <= 7) return 'border-l-red-500 bg-red-50 dark:bg-red-900/20'
    if (days <= 15) return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
    return 'border-l-green-500 bg-green-50 dark:bg-green-900/20'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <PageHeader title="Bem-vindo, Parceiro!" subtitle="Visão geral de seu desempenho e operações" />

      {/* Key Metrics - Using StatCard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Clínicas"
          value={stats.totalClients}
          subtitle={`${stats.activeClients} ativos`}
          icon={Users}
          iconColor="text-blue-600 dark:text-blue-400"
        />
        <StatCard
          title="Receita Mensal"
          value={`R$ ${stats.monthlyRevenue.toLocaleString('pt-BR')}`}
          icon={DollarSign}
          iconColor="text-green-600 dark:text-green-400"
          trend={stats.revenueGrowth ? {
            value: stats.revenueGrowth,
            label: 'vs. mês anterior',
            isPositiveGood: true
          } : undefined}
        />
        <StatCard
          title="Documentos Pendentes"
          value={stats.pendingDocuments}
          subtitle="aguardando revisão"
          icon={FileText}
          iconColor="text-orange-600 dark:text-orange-400"
        />
        <StatCard
          title="Taxa de Compliance"
          value={`${stats.complianceRate.toFixed(1)}%`}
          subtitle="obrigações em dia"
          icon={Activity}
          iconColor="text-purple-600 dark:text-purple-400"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Atividades Recentes</h3>
              <Activity className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="p-6">
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${getActivityColor(activity.status)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.description}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{activity.client}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">{getTimeAgo(activity.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Nenhuma atividade recente</p>
              </div>
            )}
            <button onClick={() => navigate('/support')} className="w-full mt-4 text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
              Ver todas as atividades
            </button>
          </div>
        </div>

        {/* Upcoming Obligations */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Obrigacoes Proximas</h3>
              <AlertTriangle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="p-6">
            {obligations.length > 0 ? (
              <div className="space-y-4">
                {obligations.slice(0, 3).map((obligation) => (
                  <div key={obligation.id} className={`p-4 border-l-4 rounded-r-lg ${getUrgencyColor(obligation.daysUntilDue)}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{obligation.description}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{obligation.client}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            Vence em {obligation.daysUntilDue} dias
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white">
                            R$ {obligation.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        obligation.daysUntilDue <= 7 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                        obligation.daysUntilDue <= 15 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      }`}>
                        {obligation.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-900 dark:text-white font-medium">Nenhuma obrigacao proxima</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Suas obrigacoes fiscais aparecerao aqui</p>
              </div>
            )}
            <button onClick={() => navigate('/tax-management')} className="w-full mt-4 text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
              Ver calendario fiscal completo
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Acoes Rapidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button onClick={() => navigate('/clients')} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-800 dark:text-white">Adicionar Clínica</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Nova clinica ou medico</p>
            </div>
          </button>

          <button onClick={() => navigate('/documents')} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/40">
              <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-800 dark:text-white">Revisar Documentos</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{stats.pendingDocuments} pendentes</p>
            </div>
          </button>

          <button onClick={() => navigate('/tax-management')} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
              <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-800 dark:text-white">Calendario Fiscal</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Obrigacoes e vencimentos</p>
            </div>
          </button>

          <button onClick={() => navigate('/analytics')} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200">
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/40">
              <Activity className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-800 dark:text-white">Relatorios</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Performance e compliance</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}
